import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import DeliverableClientApprovalMedia from './deliverable-client-approval-media/DeliverableClientApprovalMedia';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import DeliverableApprovalNote from '../deliverable-content-approval-tab/deliverable-approval-note/DeliverableApprovalNote';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import MoreButton from 'components/buttons/more-button/MoreButton';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import StatusLabel from 'components/status-label/StatusLabel';
import DeliverableLooks from './deliverable-looks/DeliverableLooks';
import { ModalOpener } from 'components/modal';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import {
  editClientNote,
  getClientApprovalMedia,
  getLooks,
  resetClientApprovalDeliverable,
  resetLastCreatedLook,
  updateClientApproval
} from 'modules/deliverables/reducers/deliverableContentApproval';

import { contentApprovalLinkSelector } from 'selectors/campaign';

import { __t, applyTranslations } from 'utils/language';
import { isFeedbackProvided } from 'utils/deliverables';
import { APPROVAL_STATUSES } from 'utils/contentApproval';
import { insertIf } from 'utils/list';
import { copyTextToClipboard } from 'utils/helper/common';

import {
  Deliverable,
  DeliverableAttachment,
  DeliverableClientApproval as DeliverableClientApprovalAlias,
  DeliverableLook,
  DeliverableNote,
  MixedSelectDropdownItem,
  StatusLabelType,
  Subcampaign
} from 'types';
import {
  ContentGalleryItem
} from 'components/content-gallery/types';

import './deliverable-client-approval.scss';

applyTranslations(require('./deliverable-client-approval.i18n.json'));

type ClientApprovalDataItem = {
  assetId: string | number
};

type ClientApprovalActions = 'unpublish' | 'publish';

type OwnProps = {
  attachments: Array<DeliverableAttachment>,
  deliverable: Deliverable,
  subcampaign: Subcampaign,
  isForBrand?: boolean,
  setDeliverableModified: (isModified: boolean) => void,
  onOpenClientApproval: (openClientApproval: boolean) => void
};

type StateProps = {
  looks: Array<DeliverableLook>,
  clientApprovalData: DeliverableClientApprovalAlias,
  lastCreatedLook?: DeliverableLook,
  contentApprovalLink: string | undefined | null
};

type DispatchProps = {
  dispatchShowConfirmationModal: (
    params: {
      title?: string,
      message: string,
      confirmButtonLabel?: string,
      cancelButtonLabel?: string,
      onConfirm?: () => void,
      onCancel?: () => void,
      onClose?: () => void,
      innerContent?: React.ReactNode
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchEditClientNote: (deliverableId, noteId: number, noteValue: string) => Promise<void>,
  dispatchGetClientApprovalMedia: (offerId: number, deliverableId: number) =>
    Promise<DeliverableClientApprovalAlias>,
  dispatchGetLooks: (campaignId: number) => Promise<{ looks: Array<DeliverableLook> }>,
  dispatchUpdateClientApprovalData: (offerId: number, deliverableId: number, data, action?: ClientApprovalActions) => void,
  dispatchResetState: (deliverableId: number) => void,
  dispatchResetLastCreatedLook: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  note: string,
  noteId: number | null,
  clientAttachments: Array<ContentGalleryItem>,
  includeCaption: boolean,
  displayTaskName: boolean,
  clientNotes: Array<DeliverableNote>,
  looksMenuItems: Array<MixedSelectDropdownItem>,
  isModified: boolean,
  showLooksModal: boolean
};

const msgKey = 'deliverable_client_approval';

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

class DeliverableClientApproval extends React.PureComponent<Props, State> {
  state: State = {
    note: '',
    noteId: null,
    clientAttachments: [],
    displayTaskName: false,
    includeCaption: false,
    clientNotes: [],
    looksMenuItems: [],
    isModified: false,
    showLooksModal: false
  };

  componentDidMount() {
    const {
      dispatchGetClientApprovalMedia,
      dispatchGetLooks,
      deliverable,
      subcampaign } = this.props;
    const { id } = deliverable;
    const { offer, campaign } = subcampaign;

    dispatchGetClientApprovalMedia(offer.id, id)
      .then(this.setInitialState);
    dispatchGetLooks(campaign.id);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      dispatchGetClientApprovalMedia,
      attachments,
      clientApprovalData,
      deliverable,
      looks,
      subcampaign
    } = this.props;
    const { offer } = subcampaign;
    const { look } = clientApprovalData || {};

    if (!isEqual(prevProps.clientApprovalData, clientApprovalData)) {
      this.setInitialState();
    }

    if (!isEqual(prevProps.attachments, attachments)) {
      dispatchGetClientApprovalMedia(offer.id, deliverable.id)
        .then(() => this.setInitialState());
    }

    if (!isEqual(prevProps.looks, looks)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(prevState => ({
        ...prevState,
        looksMenuItems: this.getLooks(looks || [], look)
      }));
    }
  }

  componentWillUnmount() {
    const { dispatchResetState, deliverable } = this.props;

    dispatchResetState(deliverable.id);
  }

  addNewLookItem(): Array<MixedSelectDropdownItem> {
    const { looksMenuItems } = this.state;
    const containsNewLookItem = looksMenuItems.some(item => item.id === 'add-new-look');

    return [...looksMenuItems, ...insertIf(!containsNewLookItem, {
      id: 'add-new-look',
      identifier: 'add-new-look',
      value: 'add-new-look',
      label: '+ Create new look',
      isDisabled: false,
      isFocused: false,
      isSelected: false
    })];
  }

  setInitialState = () => {
    const { clientApprovalData, looks, attachments, setDeliverableModified } = this.props;
    const { items = [], notes = [], look, displayTaskName = false, includeCaption = false } = clientApprovalData || {};

    const clientAttachments = attachments.map(attachment => {
      const selectedItem = items.find(item => item.assetId === attachment.id);

      return {
        ...attachment,
        isSelected: Boolean(selectedItem),
        approvalStatus: selectedItem?.status || 'pending'
      };
    });

    this.setState(prevState => ({
      ...prevState,
      clientAttachments,
      clientNotes: notes,
      looksMenuItems: this.getLooks(looks || [], look),
      displayTaskName,
      includeCaption,
      note: '',
      noteId: null,
      isModified: false
    }));
    setDeliverableModified(false);
  };

  getLooks(looks: Array<DeliverableLook>, selectedLook: DeliverableLook | null): Array<MixedSelectDropdownItem> {
    const { deliverable } = this.props;
    const { tag } = deliverable;

    return looks.map(look => {
      const isSelectedDefault = isNil(selectedLook) && look?.isTag && look?.title === deliverable?.tag?.name;
      const isSelectedById = !isSelectedDefault && look.id === selectedLook?.id;
      const isSelectedByTitle = !isSelectedDefault && look?.title === selectedLook?.title;
      const isSelectedByTagName = !isSelectedDefault && isSelectedByTitle && tag?.name === look.title;

      return {
        id: look.title,
        identifier: look.title,
        value: look.title,
        label: look.title,
        isDisabled: false,
        isFocused: false,
        isSelected: isSelectedDefault || isSelectedById || isSelectedByTitle || isSelectedByTagName
      };
    });
  }

  getStatusLabelType(): StatusLabelType {
    const { clientApprovalData } = this.props;
    const { captionStatus } = clientApprovalData;

    if (isNil(captionStatus) || captionStatus === APPROVAL_STATUSES.PENDING) {
      return 'warning';
    }

    return captionStatus === APPROVAL_STATUSES.APPROVED ? 'success' : 'danger';
  }

  getStatusLabelText(): string {
    const { clientApprovalData } = this.props;
    const { captionStatus } = clientApprovalData;

    if (isNil(captionStatus) || captionStatus === APPROVAL_STATUSES.PENDING) {
      return __t(`${msgKey}.status_label.pending`);
    }

    return isFeedbackProvided(captionStatus) ?
      __t(`${msgKey}.status_label.${captionStatus}`)
      : '';
  }

  handleModification = (isModified: boolean, state: Partial<State>) => {
    const { setDeliverableModified } = this.props;

    this.setState(prevState => ({
      ...prevState,
      ...state,
      isModified
    }));

    setDeliverableModified(isModified);
  };

  handleNewNoteChange = (value: string) => {
    const { setDeliverableModified } = this.props;

    this.setState(prevState => ({
      ...prevState,
      note: value,
      isModified: true
    }));

    setDeliverableModified(true);
  };

  handleNoteDelete = (noteId: number) => {
    const {
      deliverable,
      dispatchEditClientNote,
      dispatchHideConfirmationModal,
      dispatchShowConfirmationModal
    } = this.props;

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.delete_note_modal.title`),
      message: __t(`${msgKey}.delete_note_modal.info`),
      confirmButtonLabel: __t(`${msgKey}.delete_note_modal.remove_btn`),
      onClose: () => dispatchHideConfirmationModal(),
      onCancel: () => dispatchHideConfirmationModal(),
      onConfirm: async () => {
        if (!noteId) {
          return;
        }

        dispatchHideConfirmationModal();
        await dispatchEditClientNote(deliverable.id, noteId, '');
      },
      innerContent: (
        <div className="deliverable-client-approval__modal-confirmation">
          {__t(`${msgKey}.delete_note_modal.confirmation_message`)}
        </div>
      )
    });
  };

  handleNoteEdit = (id: number, text: string) => {
    const { note } = this.state;
    const { dispatchHideConfirmationModal, dispatchShowConfirmationModal } = this.props;

    if (note) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.discard_unsaved_note_modal.title`),
        message: __t(`${msgKey}.discard_unsaved_note_modal.info`),
        confirmButtonLabel: __t(`${msgKey}.discard_unsaved_note_modal.discard_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.setState({
            note: text,
            noteId: id
          });

          dispatchHideConfirmationModal();
        },
        innerContent: (
          <div className="deliverable-client-approval__modal-confirmation">
            {__t(`${msgKey}.discard_unsaved_note_modal.confirmation_message`)}
          </div>
        )
      });
    } else {
      this.setState({
        note: text,
        noteId: id,
        isModified: true
      });
    }
  };

  setClientAttachments = (isSelected: boolean, deliverableAttachmentId?: number | string) => {
    const { setDeliverableModified } = this.props;

    this.setState(prevState => ({
      ...prevState,
      clientAttachments: prevState.clientAttachments.map(attachment => ({
        ...attachment,
        isSelected: attachment.id === deliverableAttachmentId ? isSelected : attachment.isSelected
      })),
      isModified: true
    }));

    setDeliverableModified(true);
  };

  handleCaptionStatusChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
    const { clientApprovalData, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { includeCaption } = this.state;
    const target = event.currentTarget;

    const isCaptionSentForApproval = isFeedbackProvided(clientApprovalData?.captionStatus);

    if (!target.checked && isCaptionSentForApproval) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.change_look_modal.title`),
        message: __t(`${msgKey}.change_look_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.change_look_modal.confirm_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.handleModification(true, { includeCaption: !includeCaption });
          dispatchHideConfirmationModal();
        },
        innerContent: (
          <div className="deliverable-client-approval__modal-confirmation">
            {__t(`${msgKey}.change_look_modal.confirmation_msg`)}
          </div>
        )
      });
    } else {
      this.handleModification(true, { includeCaption: !includeCaption });
    }
  };

  handleAttachmentsSelectionChange = (
    isSelected: boolean,
    deliverableAttachmentId?: number | string,
    event?: React.MouseEvent
  ) => {
    event?.preventDefault();

    const { clientApprovalData } = this.props;
    const { items } = clientApprovalData;
    const isItemSentForApproval = deliverableAttachmentId ?
      items.find(item => item.assetId === deliverableAttachmentId && isFeedbackProvided(item.status)) :
      !!items.length && items.find(item => isFeedbackProvided(item.status));

    if (!isSelected && isItemSentForApproval) {
      const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;

      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.change_look_modal.title`),
        message: __t(`${msgKey}.change_look_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.change_look_modal.confirm_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.setClientAttachments(isSelected, deliverableAttachmentId);

          dispatchHideConfirmationModal();
        },
        innerContent: (
          <div className="deliverable-client-approval__modal-confirmation">
            {__t(`${msgKey}.change_look_modal.confirmation_msg`)}
          </div>
        )
      });
    } else {
      this.setClientAttachments(isSelected, deliverableAttachmentId);
    }
  };

  handleSelectDeselectToggle = (isSelected: boolean): void => {
    const { setDeliverableModified } = this.props;

    this.setState(prevState => ({
      ...prevState,
      clientAttachments: prevState.clientAttachments.map(attachment => ({
        ...attachment,
        isSelected
      })),
      isModified: true
    }));

    setDeliverableModified(true);
  };

  handlePublish = () => {
    const { looks, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { clientAttachments, looksMenuItems, includeCaption } = this.state;

    const selectedLook = looks.find(look =>
      looksMenuItems.find(lookItem => lookItem.isSelected && lookItem.id === look.title));

    const items = clientAttachments
      .filter(attachment => attachment.isSelected).map(attachment => ({ assetId: attachment.id }));

    if (!items.length && !includeCaption) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.attach_items_modal.title`),
        message: __t(`${msgKey}.attach_items_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.attach_items_modal.confirm_btn`),
        cancelButtonLabel: __t(`${msgKey}.buttons.cancel`),
        innerContent: (
          <div className="deliverable-client-approval__modal-confirmation">
            {__t(`${msgKey}.attach_items_modal.message_confirm`)}
          </div>
        ),
        onClose: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.handleDispatchUpdateClientApprovalData(items, selectedLook, 'unpublish');
          dispatchHideConfirmationModal();
        },
        onCancel: () => {
          dispatchHideConfirmationModal();
        }
      });

      return;
    }

    if (!selectedLook) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.create_look_modal.title`),
        message: __t(`${msgKey}.create_look_modal.${looks.length ? 'message_with_look' : 'message_no_look'}`),
        confirmButtonLabel: __t(`${msgKey}.create_look_modal.${looks.length ? 'confirm_btn' : 'create_look_btn'}`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          if (!looks.length) {
            this.handleManageLooks();
          }

          dispatchHideConfirmationModal();
        }
      });

      return;
    }

    this.handleDispatchUpdateClientApprovalData(items, selectedLook);
  };

  handleDispatchUpdateClientApprovalData = async (
    items: Array<ClientApprovalDataItem>,
    selectedLook: DeliverableLook | undefined,
    action?: ClientApprovalActions
  ): Promise<void> => {
    const { deliverable, subcampaign, dispatchEditClientNote, dispatchUpdateClientApprovalData } = this.props;
    const { note, noteId, includeCaption, displayTaskName } = this.state;
    const { offer } = subcampaign;
    const noteValue = note || undefined;
    const isEditingNote = noteValue && noteId;

    if (isEditingNote) {
      await dispatchEditClientNote(deliverable.id, noteId, noteValue);
    }

    dispatchUpdateClientApprovalData(offer.id, deliverable.id, {
      items,
      includeCaption,
      displayTaskName,
      note: isEditingNote ? undefined : noteValue,
      look: selectedLook
    }, action);
  };

  handleCancelChanges = () => {
    const { setDeliverableModified } = this.props;

    this.setInitialState();
    setDeliverableModified(false);
  };

  handleManageLooks = () => {
    this.setState({ showLooksModal: true });
  };

  handleGetClientPageLink = (updatedData: any): void => {
    const clientPageUrlKey = updatedData.clientPageUrlKey || null;

    if (clientPageUrlKey) {
      copyTextToClipboard(`[data-copy-key="${clientPageUrlKey}"]`);
    }
  };

  handleChangeInLook = (updatedItems, _, selectedItem) => {
    const { clientApprovalData, setDeliverableModified, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { look, items } = clientApprovalData;
    const hasClientProvidedFeedback = items.find(item => isFeedbackProvided(item.status));

    if (selectedItem.identifier === 'add-new-look') {
      this.handleManageLooks();

      return;
    }

    if (look && look.title !== selectedItem.title) {
      const looksMenuItems = [
        ...updatedItems
          .filter(item => item.id !== 'add-new-look')
          .map(item => ({
            ...item,
            isSelected: selectedItem.id === item.id && selectedItem.isSelected
          }))
      ];

      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.change_look_modal.title`),
        message: __t(`${msgKey}.change_look_modal.${hasClientProvidedFeedback ? 'feedback_msg' : 'without_feedback_msg'}`),
        confirmButtonLabel: __t(`${msgKey}.change_look_modal.confirm_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.setState(prevState => ({
            ...prevState,
            clientAttachments: prevState.clientAttachments.map(attachment => ({
              ...attachment,
              isSelected: false
            })),
            isModified: true,
            includeCaption: false,
            looksMenuItems
          }));

          setDeliverableModified(true);
          dispatchHideConfirmationModal();
        },
        innerContent: (
          <div className="deliverable-client-approval__modal-confirmation">
            {__t(`${msgKey}.change_look_modal.confirmation_msg`)}
          </div>
        )
      });
    } else {
      this.handleModification(true, {
        looksMenuItems: [...updatedItems]
      });
    }
  };

  private handleLooksModalClose = () => {
    const { lastCreatedLook, dispatchResetLastCreatedLook } = this.props;
    const { looksMenuItems } = this.state;

    this.setState({ showLooksModal: false });
    dispatchResetLastCreatedLook();

    const hasNoSelectedLooks = looksMenuItems.every(item => !item.isSelected);

    if (lastCreatedLook && hasNoSelectedLooks) {
      this.handleModification(true, {
        looksMenuItems: looksMenuItems.map(item => ({
          ...item,
          isSelected: item.value === lastCreatedLook.title
        }))
      });
    }
  };

  renderModal() {
    const { subcampaign } = this.props;
    const { showLooksModal } = this.state;

    return (
      <ModalOpener
        open={showLooksModal}
        showClose
        width="small"
        onClose={this.handleLooksModalClose}
      >
        <DeliverableLooks
          subcampaign={subcampaign}
          onClose={this.handleLooksModalClose}
        />
      </ModalOpener>
    );
  }

  renderCloseIcon(): React.ReactNode {
    const { onOpenClientApproval } = this.props;

    return (
      <div className="deliverable-client-approval__close">
        <SVGDynamicIcon
          name="cross-custom-fg"
          width={12}
          height={12}
          onClick={() => onOpenClientApproval(false)}
          customColor="grey"
        />
      </div>
    );
  }

  renderFooter(): React.ReactNode {
    const { deliverable } = this.props;
    const { isModified } = this.state;

    return (
      <div className="deliverable-client-approval__footer">
        {
          isModified && (
            <ActionButton
              color="tertiary"
              onClick={this.handleCancelChanges}
            >
              {__t(`${msgKey}.buttons.cancel`)}
            </ActionButton>
          )
        }
        <ActionButton
          color="primary"
          onClick={this.handlePublish}
        >
          {__t(`${msgKey}.buttons.publish`)}
        </ActionButton>
        <div className="deliverable-client-approval__context-menu">
          <MoreButtonWithMenu
            width={15}
            contextMenuProps={{
              items: [{
                text: __t(`${msgKey}.context_menu.manage_looks`),
                onClick: this.handleManageLooks
              }, {
                text: __t(`${msgKey}.context_menu.get_client_page_look`),
                onClick: this.handleGetClientPageLink
              }],
              data: {
                clientPageUrlKey: `deliverable-client-approval__link-to-client--${deliverable.id}`
              }
            }}
            iconHeight={24}
            iconWidth={6}
          />
        </div>
      </div>
    );
  }

  renderApprovalNote(): React.ReactNode {
    const { deliverable, isForBrand } = this.props;
    const { note, noteId, clientNotes } = this.state;
    const { id } = deliverable;

    return (
      <div className="deliverable-client-approval__approval-note">
        <DeliverableApprovalNote
          notes={clientNotes}
          uniqueId={`deliverable-client-approval-notes-${id}`}
          noteValue={note}
          noteId={noteId}
          isForBrand={isForBrand}
          onNewNoteChange={this.handleNewNoteChange}
          type="client"
          onNoteEdit={this.handleNoteEdit}
          onNoteDelete={this.handleNoteDelete}
        />
      </div>
    );
  }

  renderCaption(): React.ReactNode {
    const { deliverable, clientApprovalData } = this.props;
    const { includeCaption } = this.state;
    const { id, contentApproval } = deliverable;
    const { includeCaption: includeCaptionFromClientData = false } = clientApprovalData || {};

    return (
      <React.Fragment>
        <div className="deliverable-client-approval__caption-header">
          {__t(`${msgKey}.caption_header`)}
        </div>
        <div className="deliverable-client-approval__caption-approval">
          <InputCheckbox
            key={`deliverable-client-approval-caption-checkbox-${id}`}
            id={`deliverable-client-approval-caption-checkbox-${id}`}
            name="deliverable-client-approval-caption-checkbox"
            value={String(includeCaption)}
            checked={includeCaption}
            onChange={this.handleCaptionStatusChange}
          />
          <div className="deliverable-client-approval__caption">
            {contentApproval?.caption.text}
          </div>
          {
            includeCaptionFromClientData && (
              <StatusLabel
                size="nano"
                type={this.getStatusLabelType()}
                label={this.getStatusLabelText()}
                className="deliverable-client-approval__caption-status"
              />
            )
          }
        </div>
      </React.Fragment>
    );
  }

  renderMediaItems(): React.ReactNode {
    const { clientAttachments } = this.state;

    if (isEmpty(clientAttachments)) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="deliverable-client-approval__header">
          {__t(`${msgKey}.header`)}
        </div>
        <DeliverableClientApprovalMedia
          attachments={clientAttachments}
          onSelectionChange={this.handleAttachmentsSelectionChange}
          onSelectDeselectToggle={this.handleSelectDeselectToggle}
        />
      </React.Fragment>
    );
  }

  renderLooks(): React.ReactNode {
    const { deliverable } = this.props;
    const { displayTaskName } = this.state;
    const { id } = deliverable;
    const items = this.addNewLookItem();

    return (
      <React.Fragment>
        <div className="deliverable-client-approval__look-header">
          {__t(`${msgKey}.look_header`)}
        </div>
        <div className="deliverable-client-approval__look-container">
          <MixedSelectDropdown
            items={items}
            name="deliverable-client-approval-looks"
            withSearchable
            placeholder={__t(`${msgKey}.look_header`)}
            onItemsToggle={this.handleChangeInLook}
            className="deliverable-client-approval__look-select"
          />
          <InputCheckbox
            key={`deliverable-client-approval-task-name-checkbox-${id}`}
            id={`deliverable-client-approval-task-name-checkbox-${id}`}
            name="deliverable-client-approval-task-name-checkbox"
            value={String(displayTaskName)}
            checked={displayTaskName}
            labelClass="deliverable-client-approval-task-name__label"
            label={__t(`${msgKey}.display_task_name`)}
            onChange={() => this.handleModification(true, { displayTaskName: !displayTaskName })}
            containerClass="deliverable-client-approval__task-name-checkbox"
          />
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { deliverable, contentApprovalLink } = this.props;
    const { contentApproval } = deliverable;

    return (
      <div className="deliverable-client-approval__main">
        {this.renderCloseIcon()}
        {this.renderMediaItems()}
        {
          contentApproval?.caption?.text &&
            this.renderCaption()
        }
        {this.renderLooks()}
        {this.renderApprovalNote()}
        {this.renderFooter()}
        {this.renderModal()}
        <div
          className="deliverable-client-approval__link-to-client"
          data-copy-key={`deliverable-client-approval__link-to-client--${deliverable.id}`}
        >
          {`${window.location.origin}${contentApprovalLink}`}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, { deliverable, subcampaign }): StateProps => {
  const { deliverableContentApproval } = state;

  return {
    clientApprovalData: deliverableContentApproval.clientApprovalData[deliverable.id],
    looks: deliverableContentApproval.looks[subcampaign?.campaign?.id] || [],
    lastCreatedLook: deliverableContentApproval.lastCreatedLook,
    contentApprovalLink: contentApprovalLinkSelector(state)
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetState: (deliverableId: number) => dispatch(resetClientApprovalDeliverable(deliverableId)),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchGetClientApprovalMedia: (offerId: number, deliverableId: number) =>
    dispatch(getClientApprovalMedia(offerId, deliverableId)),
  dispatchGetLooks: (campaignId: number) => (
    dispatch(getLooks(campaignId))
  ),
  dispatchEditClientNote: (deliverableId: number, noteId: number, noteValue: string) => (
    dispatch(editClientNote(deliverableId, noteId, noteValue))
  ),
  dispatchUpdateClientApprovalData: (offerId: number, deliverableId: number, data, action?: ClientApprovalActions) => (
    dispatch(updateClientApproval(offerId, deliverableId, data, action))
  ),
  dispatchResetLastCreatedLook: () => (
    dispatch(resetLastCreatedLook())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableClientApproval);
